import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout, getParentLayout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'

const userInfo = JSON.parse(localStorage.getItem('user') || '{}').userInfo
const isRsApp = userInfo?.bizAppcode == 'rsapp'
const { t } = useI18n()
/**
* redirect: noredirect        当设置 noredirect 的时候该路由在面包屑导航中不可被点击
* name:'router-name'          设定路由的名字，一定要填写不然使用<keep-alive>时会出现各种问题
* meta : {
    hidden: true              当设置 true 的时候该路由不会再侧边栏出现 如404，login等页面(默认 false)
    alwaysShow: true          当你一个路由下面的 children 声明的路由大于1个时，自动会变成嵌套的模式，
                              只有一个时，会将那个子路由当做根路由显示在侧边栏，
                              若你想不管路由下面的 children 声明的个数都显示你的根路由，
                              你可以设置 alwaysShow: true，这样它就会忽略之前定义的规则，
                              一直显示根路由(默认 false)
    title: 'title'            设置该路由在侧边栏和面包屑中展示的名字
    icon: 'svg-name'          设置该路由的图标
    noCache: true             如果设置为true，则不会被 <keep-alive> 缓存(默认 false)
    breadcrumb: false         如果设置为false，则不会在breadcrumb面包屑中显示(默认 true)
    affix: true               如果设置为true，则会一直固定在tag项中(默认 false)
    noTagsView: true          如果设置为true，则不会出现在tag中(默认 false)
    activeMenu: '/dashboard'  显示高亮的路由路径
    canTo: true               设置为true即使hidden为true，也依然可以进行路由跳转(默认 false)
    permission: ['edit','add', 'delete']    设置该路由的权限
  }
**/
export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/business/index',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'Redirect',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: t('router.login'),
      noTagsView: true
    }
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/business',
    component: Layout,
    redirect: '/business/index',
    name: 'Business',
    meta: {
      title: '首页',
      icon: 'fluent:home-24-filled'
    },
    children: [
      {
        path: 'index',
        component: () => import('@/views/business/dashboard/index.vue'),
        name: 'Dashboard',
        meta: {
          title: '首页',
          icon: 'fluent:home-24-filled'
        }
      }
    ]
  },
  {
    path: '/order',
    component: Layout,
    name: 'Order',
    redirect: '/order/list',
    meta: {
      title: '订单管理',
      icon: 'ri:file-list-fill'
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/business/order/list.vue'),
        name: 'OrderList',
        meta: {
          title: '订单列表',
          icon: 'ep:list'
        }
      },
      {
        path: 'detail',
        name: 'OrderDetail',
        component: () => import('@/views/business/order/detail.vue'),
        meta: {
          title: '订单详情',
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/order/list'
        }
      },
      {
        path: 'blacklist',
        component: () => import('@/views/business/order/blacklist.vue'),
        name: 'BlackList',
        meta: {
          title: '黑名单列表',
          icon: 'fluent:clipboard-error-24-filled'
        }
      }
    ]
  },
  {
    path: '/agent',
    component: Layout,
    name: 'Agent',
    redirect: '/agent/list',
    meta: {
      title: '代理管理',
      icon: 'fa6-solid:user-group'
      // alwaysShow: true
    },
    children: [
      {
        path: 'list',
        name: 'AgentList',
        component: () => import('@/views/business/agent/list.vue'),
        meta: {
          title: '代理列表',
          icon: 'fa6-solid:user-group'
        }
      }
    ]
  },
  {
    path: '/withdrawal',
    component: Layout,
    name: 'Withdrawal',
    redirect: '/withdrawal/list',
    meta: {
      title: '提现管理',
      icon: 'ri:wallet-fill'
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/business/withdrawal/list.vue'),
        name: 'WithdrawalList',
        meta: {
          title: '提现管理',
          icon: 'ri:wallet-fill'
        }
      }
    ]
  },
  {
    path: '/platform',
    component: Layout,
    name: 'Platform',
    redirect: '/platform/notice',
    meta: {
      title: '平台管理',
      icon: 'garden:platform-26'
    },
    children: [
      {
        path: 'notice',
        component: () => import('@/views/business/platform/notice.vue'),
        name: 'PlatformList',
        meta: {
          title: '公告管理',
          icon: 'icon-park-solid:volume-notice'
        }
      },
      {
        path: 'essay',
        component: () => import('@/views/business/platform/essay.vue'),
        name: 'PlatformEssay',
        meta: {
          title: '资讯管理',
          icon: 'mdi:notice-board'
        }
      }
    ]
  },
  {
    path: '/complaint',
    component: Layout,
    name: 'Complaint',
    redirect: '/complaint/index',
    meta: {
      title: '客诉管理',
      icon: 'iconoir:chat-bubble-warning-solid',
      hidden: !isRsApp
    },
    children: [
      {
        path: 'index',
        component: () => import('@/views/business/complaint/index.vue'),
        name: 'ComplaintList',
        meta: {
          title: '客诉管理',
          icon: 'iconoir:chat-bubble-warning-solid'
        }
      },
      {
        path: 'detail',
        component: () => import('@/views/business/complaint/detail.vue'),
        name: 'ComplaintDetail',
        meta: {
          title: '客诉详情',
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/complaint/index'
        }
      }
    ]
  },
  {
    path: '/member',
    component: Layout,
    name: 'Member',
    redirect: '/member/index',
    meta: {
      title: '会员配置',
      icon: 'iconoir:chat-bubble-warning-solid',
      hidden: !isRsApp
    },
    children: [
      {
        path: 'index',
        component: () => import('@/views/business/member/index.vue'),
        name: 'MemberList',
        meta: {
          title: '会员配置',
          icon: 'icon-park-twotone:crown-three'
        }
      }
    ]
  },
  {
    path: '/system',
    component: Layout,
    name: 'System',
    redirect: '/system/index',
    meta: {
      title: '系统管理',
      icon: 'icon-park-solid:setting'
    },
    children: [
      {
        path: 'balance',
        component: () => import('@/views/business/system/balance.vue'),
        name: 'SystemBalance',
        meta: {
          title: 'API余额',
          icon: 'ic:outline-account-balance-wallet'
        }
      },
      {
        path: 'index',
        component: () => import('@/views/business/system/index.vue'),
        name: 'SystemIndex',
        meta: {
          title: '系统配置',
          icon: 'ic:baseline-display-settings'
        }
      },
      {
        path: 'people',
        component: () => import('@/views/business/system/people.vue'),
        name: 'SystemPeople',
        meta: {
          title: '联系人',
          icon: 'octicon:people-16'
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  const resetWhiteNameList = ['Redirect', 'Login', 'NoFind', 'Root']
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !resetWhiteNameList.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
